/**
 * VehicleSelector - Seletor de veículo com busca server-side
 *
 * Reaproveita a RPC `list_tenant_vehicles` (mesma usada na listagem de
 * veículos), garantindo que o frontend nunca faça SELECT direto na tabela.
 *
 * @version 1.3.0
 * RELEASE: 2026-08-21 v1.3.0 - Escopo por cliente/contato (`scopeCustomerId`/`scopeContactId`):
 *            quando informado, lista apenas os veículos vinculados àquele cliente/contato
 *            (RPC list_customer_vehicles) com busca aplicada localmente
 * RELEASE: 2026-08-09 v1.2.0 - Nova prop `excludeIds` para ocultar veículos já vinculados
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (vínculo de veículo em tarefas)
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Car } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCustomerVehicles } from '@/hooks/useCustomerVehicles';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/** Opção mínima de veículo exibida no seletor */
interface VehicleOption {
  id: string;
  plate: string | null;
  brand_name: string | null;
  model_name: string | null;
  year: number | null;
}

interface VehicleSelectorProps {
  value?: string | null;
  onSelect: (vehicleId: string | null) => void;
  /** v1.1.0 — devolve também o veículo completo (rótulos para chips) */
  onSelectVehicle?: (vehicle: VehicleOption | null) => void;
  /** Rótulo do veículo já vinculado (vindo da RPC de edição) */
  fallbackLabel?: string | null;
  /** v1.2.0 — IDs de veículos que não devem aparecer na listagem (já vinculados) */
  excludeIds?: string[];
  /** v1.3.0 — quando informado, lista apenas os veículos deste cliente */
  scopeCustomerId?: string | null;
  /** v1.3.0 — quando informado, lista apenas os veículos deste contato */
  scopeContactId?: string | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}


export type { VehicleOption };

/** Monta o rótulo "PLACA — Marca Modelo (ano)" */
function buildLabel(v: VehicleOption): string {
  const desc = [v.brand_name, v.model_name].filter(Boolean).join(' ');
  const year = v.year ? ` (${v.year})` : '';
  if (v.plate && desc) return `${v.plate} — ${desc}${year}`;
  return v.plate || `${desc}${year}` || 'Veículo';
}

export function VehicleSelector({
  value,
  onSelect,
  onSelectVehicle,
  fallbackLabel,
  excludeIds,
  scopeCustomerId,
  scopeContactId,

  placeholder = 'Selecionar veículo...',
  disabled = false,
  className,
}: VehicleSelectorProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Rótulo do item escolhido nesta sessão (evita nova consulta só para exibir)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // v1.3.0 — modo escopado: só busca veículos do cliente/contato selecionado
  const isScoped = !!(scopeCustomerId || scopeContactId);

  const { vehicles: scopedVehicles, isLoading: isLoadingScoped } = useCustomerVehicles(
    { customerId: scopeCustomerId ?? null, contactId: scopeContactId ?? null },
    isScoped && open,
  );

  const { data: fetchedVehicles = [], isLoading: isLoadingGlobal } = useQuery({
    queryKey: ['vehicles-selector', currentTenant?.id, search],
    enabled: !!currentTenant?.id && open && !isScoped,
    staleTime: 30_000,
    queryFn: async (): Promise<VehicleOption[]> => {
      const { data, error } = await (supabase.rpc as any)('list_tenant_vehicles', {
        p_search: search.trim() || null,
        p_vehicle_type: null,
        p_category_id: null,
        p_page: 1,
        p_page_size: 20,
        p_tenant_hint: currentTenant!.id,
      });
      if (error) throw error;
      const payload = (data || {}) as { items?: VehicleOption[] };
      return payload.items ?? [];
    },
  });

  const isLoading = isScoped ? isLoadingScoped : isLoadingGlobal;

  // v1.2.0 — remove da listagem os veículos já vinculados (evita confusão do usuário)
  // v1.3.0 — no modo escopado a busca digitada é filtrada localmente
  const vehicles = useMemo(() => {
    const base: VehicleOption[] = isScoped
      ? (scopedVehicles as unknown as VehicleOption[])
      : fetchedVehicles;

    let list = base;

    if (isScoped && search.trim()) {
      const term = search.trim().toLowerCase();
      list = list.filter((v) =>
        [v.plate, v.brand_name, v.model_name]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term)),
      );
    }

    if (!excludeIds?.length) return list;
    const blocked = new Set(excludeIds);
    return list.filter((v) => !blocked.has(v.id));
  }, [isScoped, scopedVehicles, fetchedVehicles, search, excludeIds]);


  // Rótulo exibido no botão: seleção atual > rótulo vindo do backend
  const displayLabel = useMemo(
    () => (value ? selectedLabel || fallbackLabel || 'Veículo vinculado' : null),
    [value, selectedLabel, fallbackLabel],
  );

  /** Handler de seleção (null = remover vínculo) */
  const handleSelect = (vehicle: VehicleOption | null) => {
    if (vehicle) {
      onSelect(vehicle.id);
      setSelectedLabel(buildLabel(vehicle));
    } else {
      onSelect(null);
      setSelectedLabel(null);
    }
    onSelectVehicle?.(vehicle);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          <div className="flex items-center gap-2 truncate">
            <Car className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className={cn('truncate', !displayLabel && 'text-muted-foreground')}>
              {displayLabel || placeholder}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por placa, marca ou modelo..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? 'Buscando...'
                : isScoped
                  ? 'Nenhum veículo vinculado a este cliente.'
                  : excludeIds?.length
                    ? 'Nenhum veículo disponível para vincular.'
                    : 'Nenhum veículo encontrado.'}
            </CommandEmpty>

            <CommandGroup>
              {value && (
                <CommandItem
                  value="clear"
                  onSelect={() => handleSelect(null)}
                  className="text-muted-foreground"
                >
                  <Car className="mr-2 h-4 w-4" />
                  Nenhum veículo
                </CommandItem>
              )}
              {vehicles.map((vehicle) => (
                <CommandItem
                  key={vehicle.id}
                  value={vehicle.id}
                  onSelect={() => handleSelect(vehicle)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                    {vehicle.plate && (
                      <span className="font-mono text-xs text-muted-foreground shrink-0">
                        {vehicle.plate}
                      </span>
                    )}
                    <span className="truncate font-medium">
                      {[vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' ') || '—'}
                    </span>
                  </div>
                  <Check
                    className={cn('ml-2 h-4 w-4', value === vehicle.id ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
